import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from './ui/form';
import { DialogFooter } from './ui/dialog';
import { toast } from './ui/use-toast';
import { Checkbox } from './ui/checkbox';
import { Loader2, ArrowLeft, X as XIcon } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getBaseUrl } from '@/lib/api';

// UQC Options (unchanged)
const UQC_OPTIONS = [
  'BAGS', 'BALE', 'BUNDLES', 'BUCKLES', 'BILLION OF UNITS', 'BOX', 'BOTTLES', 'BUNCHES', 
  'CANS', 'CUBIC METERS', 'CUBIC CENTIMETER', 'CENTIMETERS', 'CARTONS', 'DOZENS', 'DRUMS', 
  'GREAT GROSS', 'GRAMMES', 'GROSS', 'GROSS YARDS', 'KILOGRAMS', 'KILOLITRE', 'MILILITRE', 
  'METERS', 'METRIC TON', 'NUMBERS', 'PACKS', 'PIECES', 'PAIRS', 'QUINTAL', 'ROLLS', 
  'SETS', 'SQUARE FEET', 'SQUARE METERS', 'SQUARE YARDS', 'TABLETS', 'TEN GROSS', 
  'THOUSANDS', 'TONNES', 'TUBES', 'US GALLONS', 'UNITS', 'YARDS', 'OTHERS'
];

// Product form schema - base schema without LocationID
const baseProductSchema = z.object({
  Title: z.string().min(1, "Product name is required"),
  SgstPer: z.coerce.number().min(0, "SGST% must be 0 or greater").default(0),
  CGstPer: z.coerce.number().min(0, "CGST% must be 0 or greater").default(0),
  IgstPer: z.coerce.number().min(0, "IGST% must be 0 or greater").default(0),
  CessPer: z.coerce.number().min(0, "Cess% must be 0 or greater").default(0),
  MRP: z.coerce.number().min(0, "SP Inc GST must be 0 or greater").default(0),
  SPExclGST: z.coerce.number().min(0, "SP Excl GST must be 0 or greater").default(0),
  CostPrice: z.coerce.number().min(0, "Cost Price must be 0 or greater").default(0),
  NetQty: z.coerce.number().min(0, "Basic Price must be 0 or greater").default(0),
  UQCName: z.string().min(1, "UQC is required"),
  Category: z.string().min(1, "Category is required"),
  Barcode: z.string().min(1, "Barcode is required"),
  DiscountPer: z.coerce.number().min(0, "Discount% must be 0 or greater").default(0),
  Discount: z.coerce.number().min(0, "Discount must be 0 or greater").default(0),
  HSN: z.string().min(1, "HSN/SAC is required").default("1000"),
  BrandName: z.string().optional(),
  Size: z.string().optional(),
  Articlecode: z.string().optional(),
  Color: z.string().optional(),
  Style: z.string().optional(),
  Stylecode: z.string().optional(),
  Extra: z.string().optional(),
  Available: z.coerce.number().min(0, "OP Stock must be 0 or greater").default(0),
  MinStQty: z.coerce.number().min(0, "ROL must be 0 or greater").default(0),
  imgStr: z.string().optional().or(z.literal("")),
  isService: z.boolean().default(false),
  hasSerial: z.boolean().default(false),
  isTraceable: z.boolean().default(false),
  Gender: z.enum(["", "male", "female", "other"]).optional(),
  PMargin: z.coerce.number().min(0, "Profit Margin must be 0 or greater").default(0),
  Rack: z.string().optional(),
  Shelf: z.string().optional(),
});

// Add LocationID only for add mode
const productSchema = baseProductSchema.extend({
  LocationID: z.preprocess(
    (val) => (val === '' || val === undefined || val === null ? 0 : Number(val)),
    z.number().refine((val) => val > 0, { message: 'Location is required' })
  ),
});

type ProductFormValues = z.infer<typeof productSchema> & {
  LocationID?: number;
};

interface SaleProductEntryFormProps {
  token: string;
  onSuccess: () => void;
  onCancel: () => void;
  showFooter?: boolean;
  mode?: 'add' | 'edit';
  initialValues?: Partial<ProductFormValues> & { Code?: number };
  baseUrl?: string;
}

interface Category {
  id: number;
  Category: string;
}

interface Location {
  ID: number;
  Name: string;
}

const SaleProductEntryForm = forwardRef<SaleProductEntryFormHandle, SaleProductEntryFormProps>(({ token, onSuccess, onCancel, showFooter, mode = 'add', initialValues, baseUrl: propBaseUrl }, ref) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const baseUrl = propBaseUrl || getBaseUrl();
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = location.state;

  // Helper function to determine where to navigate after success
  const getReturnDestination = () => {
    console.log('SaleProductEntryForm: getReturnDestination called with returnState:', returnState);
    
    // Check if we have return state from navigation
    if (returnState?.returnTo) {
      // If returnTo is a special identifier for direct return, navigate directly to sales invoice
      if (returnState.returnTo === 'back') {
        const destination = {
          path: '/',
          state: { 
            scrollToSection: returnState.returnSection || 'products',
            productAdded: true,
            returnToSalesInvoice: true
          } as any
        };
        console.log('SaleProductEntryForm: Using direct navigation to sales invoice with destination:', destination);
        return destination;
      }
      
      const destination = {
        path: returnState.returnTo,
        state: { 
          scrollToSection: returnState.returnSection || 'products',
          productAdded: true 
        } as any
      };
      console.log('SaleProductEntryForm: Using returnTo with destination:', destination);
      return destination;
    }
    
    // Fallback to root with product section focus
    const fallbackDestination = {
      path: '/',
      state: { scrollToSection: 'products' } as any
    };
    console.log('SaleProductEntryForm: Using fallback destination:', fallbackDestination);
    return fallbackDestination;
  };

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(mode === 'edit' ? baseProductSchema : productSchema),
    defaultValues: initialValues || {
      Title: '',
      SgstPer: 0,
      CGstPer: 0,
      IgstPer: 0,
      CessPer: 0,
      MRP: 0,
      SPExclGST: 0,
      CostPrice: 0,
      NetQty: 0,
      UQCName: 'PIECES',
      Category: '',
      Barcode: '',
      DiscountPer: 0,
      Discount: 0,
      HSN: '1000',
      BrandName: '',
      Size: '',
      Articlecode: '',
      Color: '',
      Style: '',
      Stylecode: '',
      Extra: '',
      Available: 0,
      MinStQty: 0,
      imgStr: '',
      isService: false,
      hasSerial: false,
      isTraceable: false,
      Gender: '',
      PMargin: 0,
      Rack: '',
      Shelf: '',
    },
  });

  const formRef = useRef<HTMLFormElement>(null);
  const lastChangedField = useRef<'MRP' | 'SPExclGST' | null>(null);

  useImperativeHandle(ref, () => ({
    submit: () => {
      console.log('Submitting form via ref');
      if (formRef.current) {
        formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      } else {
        console.error('Form reference is not available');
      }
    }
  }));

  // Fetch categories and locations
  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers: Record<string, string> = {};
        if (authToken && authToken !== "Bearer ") {
          headers['Authorization'] = authToken;
        }
        const categoriesRes = await fetch(`${baseUrl}/api/Categories`, { headers });
        if (categoriesRes.ok) {
          const categoriesData = await categoriesRes.json();
          setCategories(categoriesData.data || []);
        } else {
          console.error('Failed to fetch categories:', categoriesRes.status, categoriesRes.statusText);
        }

        const locationsRes = await fetch(`${baseUrl}/api/Locations`, {
          headers: { 'Authorization': authToken }
        });
        if (locationsRes.ok) {
          const locationsData = await locationsRes.json();
          setLocations(locationsData.data || []);
        } else {
          console.error('Failed to fetch locations:', locationsRes.status, locationsRes.statusText);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch categories or locations',
          variant: 'destructive'
        });
      }
    };

    fetchData();
  }, [baseUrl, authToken]);

  // Watch form values for calculations (unchanged)
  const watchedValues = form.watch();
  const { SgstPer, CGstPer, CessPer, MRP, SPExclGST, DiscountPer, Discount } = watchedValues;

  // Auto-calculate IGST% (unchanged)
  useEffect(() => {
    const sgstValue = parseFloat(SgstPer?.toString() || '0');
    const cgstValue = parseFloat(CGstPer?.toString() || '0');
    const igstValue = sgstValue + cgstValue;
    form.setValue('IgstPer', igstValue);
  }, [SgstPer, CGstPer, form]);

  const initialSpSyncDone = useRef(false);

  useEffect(() => {
    initialSpSyncDone.current = false;
  }, [mode, initialValues]);

  // Ensure edit form shows SPExclGST consistent with new rule
  useEffect(() => {
    if (mode === 'edit' && !initialSpSyncDone.current) {
      const mrpValue = parseFloat(form.getValues('MRP')?.toString() || '0');
      const sgstValue = parseFloat(form.getValues('SgstPer')?.toString() || '0');
      const cgstValue = parseFloat(form.getValues('CGstPer')?.toString() || '0');
      const cessValue = parseFloat(form.getValues('CessPer')?.toString() || '0');
      const totalTaxRate = (sgstValue + cgstValue + cessValue) / 100;

      if (mrpValue > 0) {
        const spExclGST = mrpValue * (1 - totalTaxRate);
        form.setValue('SPExclGST', Number(spExclGST.toFixed(2)), { shouldDirty: false });
      } else {
        form.setValue('SPExclGST', 0, { shouldDirty: false });
      }
      initialSpSyncDone.current = true;
    }
  }, [mode, form, initialValues]);

  // Synchronized two-way calculation for MRP and SPExclGST
  useEffect(() => {
    const sgstValue = parseFloat(SgstPer?.toString() || '0');
    const cgstValue = parseFloat(CGstPer?.toString() || '0');
    const cessValue = parseFloat(CessPer?.toString() || '0');
    const totalTaxRate = (sgstValue + cgstValue + cessValue) / 100;

    // Whenever MRP or tax percentages change, refresh SPExclGST unless user is directly editing it
    if (lastChangedField.current !== 'SPExclGST') {
      lastChangedField.current = 'MRP';
      if (!MRP || Number(MRP) === 0) {
        form.setValue('SPExclGST', 0, { shouldValidate: true, shouldDirty: false });
      } else {
        const spExclGST = MRP * (1 - totalTaxRate);
        form.setValue('SPExclGST', Number(spExclGST.toFixed(2)), { shouldValidate: true, shouldDirty: false });
      }
    }
  }, [MRP, SgstPer, CGstPer, CessPer, form]);

  useEffect(() => {
    const sgstValue = parseFloat(SgstPer?.toString() || '0');
    const cgstValue = parseFloat(CGstPer?.toString() || '0');
    const cessValue = parseFloat(CessPer?.toString() || '0');
    const totalTaxRate = (sgstValue + cgstValue + cessValue) / 100;

    // When SPExclGST is changed, update MRP (but only if SPExclGST was the last field changed)
    if (form.getFieldState('SPExclGST').isDirty && lastChangedField.current !== 'MRP') {
      lastChangedField.current = 'SPExclGST';
      if (!SPExclGST || Number(SPExclGST) === 0) {
        form.setValue('MRP', 0, { shouldValidate: true });
      } else {
        const denominator = 1 - totalTaxRate;
        const mrp = denominator <= 0 ? 0 : SPExclGST / denominator;
        form.setValue('MRP', Number(mrp.toFixed(2)), { shouldValidate: true });
      }
    }
  }, [SPExclGST, SgstPer, CGstPer, CessPer, form]);

  // Handle discount logic (unchanged)
  useEffect(() => {
    if (DiscountPer > 0) {
      form.setValue('Discount', 0);
    }
  }, [DiscountPer, form]);

  useEffect(() => {
    if (Discount > 0) {
      form.setValue('DiscountPer', 0);
    }
  }, [Discount, form]);

  // Generate barcode (unchanged)
  const generateBarcode = () => {
    const random = Math.floor(Math.random() * 90000) + 10000;
    form.setValue('Barcode', random.toString());
  };

  // Calculate Offer Price (unchanged)
  const calculateOfferPrice = () => {
    const sgstValue = parseFloat(SgstPer?.toString() || '0');
    const cgstValue = parseFloat(CGstPer?.toString() || '0');
    const cessValue = parseFloat(CessPer?.toString() || '0');
    const discountPercent = parseFloat(DiscountPer?.toString() || '0');
    const discountAmount = parseFloat(Discount?.toString() || '0');
    const totalTaxRate = (sgstValue + cgstValue + cessValue) / 100;

    // Ensure MRP is a valid number
    const mrpNum = typeof MRP === 'number' ? MRP : parseFloat(MRP) || 0;
    const spExclGSTNum = typeof SPExclGST === 'number' ? SPExclGST : parseFloat(SPExclGST) || 0;

    if (discountPercent > 0) {
      // Percent discount: apply to SPExclGST, then add GST
      const spExclGST = spExclGSTNum || (mrpNum / (1 + totalTaxRate));
      const discountedPrice = spExclGST * (1 - discountPercent / 100);
      return Number((discountedPrice * (1 + totalTaxRate)).toFixed(2));
    } else if (discountAmount > 0) {
      // Rupee discount: subtract directly from MRP
      return Number((mrpNum - discountAmount).toFixed(2));
    } else {
      // No discount
      return Number((mrpNum).toFixed(2));
    }
  };

  // Parse API error (unchanged)
  const parseApiError = (result: any): string => {
    if (typeof result.message === 'string' && result.message) {
        return result.message;
    }
    if (Array.isArray(result.errors) && result.errors.length > 0) {
        return result.errors.map((e: any) => e.msg).join(', ');
    }
    return `An unknown error occurred.`;
  };
  
  const testApiConnection = async () => {
    try {
        const response = await fetch(`${baseUrl}/api/Product`, {
            method: 'OPTIONS', // Use a lightweight method
            headers: { 'Authorization': authToken }
        });
        if (response.ok) {
            toast({ title: 'API Test', description: 'Connection successful.' });
        } else {
            toast({ title: 'API Test', description: `Connection failed: ${response.status}`, variant: 'destructive' });
        }
    } catch (error) {
        toast({ title: 'API Test', description: 'Connection failed.', variant: 'destructive' });
    }
  };

  const onSubmit = async (values: ProductFormValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    form.clearErrors("Barcode");

    try {
      // Step 1: Separate quantity and location from the main product data.
      const { Available, LocationID, ...productData } = values;

      const payload = {
        ...productData,
        LocationID: String(LocationID ?? ''),
        hasSerial: productData.hasSerial ? 1 : 0,
      };
      
      Object.keys(payload).forEach(key => {
        const typedKey = key as keyof typeof payload;
        if (payload[typedKey] === '') {
            (payload as any)[typedKey] = null;
        }
      });
      
      // Step 2: Call POST /api/Product with the main data.
      const productResponse = await fetch(`${baseUrl}/api/Product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authToken },
        body: JSON.stringify(payload),
      });

      const productResult = await productResponse.json();
      console.log('Add product result:', productResult);

      if (!productResponse.ok) {
        throw new Error(parseApiError(productResult) || `Failed to add product. Status: ${productResponse.status}`);
      }

      const isSuccess = productResult.status === 1 || (productResult.status === 0 && productResult.message?.toLowerCase() === 'success');
      if (!isSuccess) {
        throw new Error(parseApiError(productResult) || 'Failed to add product.');
      }
      
      const productCode = productResult.data?.[0]?.Code;

      // Step 3: If product creation is successful and we have a quantity, call POST /api/UpdateQty.
      if (productCode && Available && Available > 0 && LocationID) {
        toast({
          title: 'Step 1/2 Complete',
          description: 'Product details saved. Now updating stock...',
        });

        const qtyPayload = {
          ProductCode: productCode,
          LocationID: LocationID,
          Qty: Available,
        };

        const qtyResponse = await fetch(`${baseUrl}/api/UpdateQty`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: authToken },
          body: JSON.stringify(qtyPayload),
        });

        const qtyResult = await qtyResponse.json();
        console.log('Update quantity result:', qtyResult);

        if (!qtyResponse.ok) {
          throw new Error(parseApiError(qtyResult) || `Failed to update quantity. Status: ${qtyResponse.status}`);
        }
        
        const isQtySuccess = qtyResult.status === 0 || qtyResult.status === 1 || qtyResult.message?.toLowerCase() === 'success';
        if (!isQtySuccess) {
           throw new Error(parseApiError(qtyResult) || 'Failed to update product quantity.');
        }

        setSuccessMessage('Product added and quantity updated successfully.');
      } else {
        setSuccessMessage('Product added successfully.');
      }

      setShowSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        setShowSuccess(false);
        // Navigate to the appropriate destination based on return state
        const destination = getReturnDestination();
        if (destination.path === 'back') {
          // Store return information in sessionStorage for the previous page to detect
          console.log('SaleProductEntryForm: Setting sessionStorage flags before back navigation');
          sessionStorage.setItem('productAdded', 'true');
          sessionStorage.setItem('returnSection', destination.state.scrollToSection || 'products');
          console.log('SaleProductEntryForm: SessionStorage set, calling window.history.back()');
          // Use window.history.back() for direct return
          window.history.back();
        } else {
          navigate(destination.path, destination.state);
        }
        // Call onSuccess callback if provided (for any additional logic)
        if (onSuccess) {
          onSuccess();
        }
      }, 2000);

    } catch (error) {
      console.error(`Error adding product:`, error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
      
      if (errorMessage.toLowerCase().includes('barcode') && errorMessage.toLowerCase().includes('exists')) {
          form.setError('Barcode', {
              type: 'manual',
              message: errorMessage,
          });
          toast({
              title: 'Duplicate Barcode',
              description: 'This barcode is already in use. Please enter a unique one.',
              variant: 'destructive',
          });
      } else {
          toast({
              title: 'Error',
              description: errorMessage,
              variant: 'destructive',
          });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onUpdate = async (values: ProductFormValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    form.clearErrors("Barcode");

    try {
      const productData = {
        Code: initialValues?.Code,
        Title: values.Title.trim(),
        Barcode: values.Barcode.trim(),
        HSN: values.HSN || "1000",
        MRP: Number(values.MRP || 0),
        CostPrice: Number(values.CostPrice || 0),
        UQCName: values.UQCName || "PIECES",
        DiscountPer: values.DiscountPer?.toString() || "0",
        Discount: Number(values.Discount || 0),
        BrandName: values.BrandName || "",
        Color: values.Color || "",
        Style: values.Style || "",
        Stylecode: values.Stylecode || "",
        Size: values.Size || "",
        NetQty: values.NetQty?.toString() || "0",
        Extra: values.Extra || "",
        Articlecode: values.Articlecode || "",
        MinStQty: values.MinStQty?.toString() || "0",
        SgstPer: Number(values.SgstPer || 0),
        CGstPer: Number(values.CGstPer || 0),
        IgstPer: Number(values.IgstPer || 0),
        Available: Number(values.Available || 0),
        Sold: 0.0,
        isTraceable: !!values.isTraceable,
        isService: !!values.isService,
        hasSerial: values.hasSerial ? 1 : 0,
        CessPer: Number(values.CessPer || 0),
        Category: values.Category || "",
        Gender: values.Gender || "",
        PMargin: Number(values.PMargin || 0),
        Rack: values.Rack || "",
        Shelf: values.Shelf || "",
        imgStr: values.imgStr || "",
        SPExclGST: Number(values.SPExclGST || 0),
      };

      const response = await fetch(`${baseUrl}/api/Product`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: authToken },
        body: JSON.stringify([productData])
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(parseApiError(result) || `HTTP error! status: ${response.status}`);
      }
      
      const isSuccess = result.status === 1 || (result.status === 0 && result.message?.toLowerCase() === 'success');
      if (!isSuccess) {
        throw new Error(parseApiError(result) || 'Unknown error during update');
      }

      setSuccessMessage('Product updated successfully!');
      setShowSuccess(true);
      toast({ title: '✅ Success', description: 'Product updated successfully', className: 'bg-green-50 border-green-200 text-green-800' });
      setTimeout(() => {
        setShowSuccess(false);
        // Navigate to the appropriate destination based on return state
        const destination = getReturnDestination();
        if (destination.path === 'back') {
          // Store return information in sessionStorage for the previous page to detect
          console.log('SaleProductEntryForm: Setting sessionStorage flags before back navigation');
          sessionStorage.setItem('productAdded', 'true');
          sessionStorage.setItem('returnSection', destination.state.scrollToSection || 'products');
          console.log('SaleProductEntryForm: SessionStorage set, calling window.history.back()');
          // Use window.history.back() for direct return
          window.history.back();
        } else {
          navigate(destination.path, destination.state);
        }
        // Call onSuccess callback if provided (for any additional logic)
        if (onSuccess) {
          onSuccess();
        }
      }, 2000);

    } catch (error) {
      console.error('Error updating product:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update product';

      if (errorMessage.toLowerCase().includes('barcode') && errorMessage.toLowerCase().includes('exists')) {
          form.setError('Barcode', {
              type: 'manual',
              message: errorMessage,
          });
          toast({
              title: 'Duplicate Barcode',
              description: 'This barcode is already in use. Please enter a unique one.',
              variant: 'destructive',
          });
      } else {
          toast({ 
              title: 'Error', 
              description: errorMessage, 
              variant: 'destructive' 
          });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    form.reset();
    setShowSuccess(false);
    setSuccessMessage('');
  };

  function formatDateDMY(dateStr?: string) {
    if (!dateStr) return 'N/A';
    const match = dateStr.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/);
    if (!match) return dateStr;
    return `${match[3]}-${match[2]}-${match[1]}`;
  }

  useEffect(() => {
    if (form.formState.isSubmitted && Object.keys(form.formState.errors).length > 0) {
      console.log('Form validation errors:', form.formState.errors);
    }
  }, [form.formState]);

  console.log('Form rendered, isSubmitting:', isSubmitting);

  // Handler for navigating back - uses return state if available
  const handleBack = () => {
    const destination = getReturnDestination();
    console.log('SaleProductEntryForm: handleBack navigating to destination:', destination);
    navigate(destination.path, destination.state);
  };

  // Handler for Cancel button: clear and navigate back
  const handleCancel = () => {
    handleClear();
    const destination = getReturnDestination();
    console.log('SaleProductEntryForm: handleCancel navigating to destination:', destination);
    navigate(destination.path, destination.state);
  };

  return (
    <Form {...form}>
      <form
        ref={formRef}
        id="product-form"
        onSubmit={form.handleSubmit(mode === 'edit' ? onUpdate : onSubmit)}
        className="space-y-6 p-4 sm:p-6 max-w-2xl mx-auto bg-white rounded-lg shadow relative android-form-container keyboard-aware-container"
      >
        {/* Responsive Back/X Button at the top */}
        <div className="mb-4 flex items-center justify-between">
          {/* Large screens: Back button with arrow and text */}
          <button
            type="button"
            onClick={handleBack}
            className="hidden lg:flex items-center text-gray-700 hover:text-blue-600 font-medium text-base"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Back
          </button>
          {/* Small screens: X icon at top-right */}
          <button
            type="button"
            onClick={handleBack}
            className="lg:hidden absolute right-4 top-4 text-gray-700 hover:text-red-600"
            aria-label="Close"
          >
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        {showSuccess && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md">
            <div className="bg-green-100 border border-green-400 text-green-800 px-4 py-3 rounded relative shadow-lg flex items-center">
              <svg className="h-6 w-6 text-green-500 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="block flex-1">{successMessage}</span>
              <button
                type="button"
                onClick={() => setShowSuccess(false)}
                className="ml-4 text-green-700 hover:text-green-900"
              >
                ×
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="Title"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">Product Name (Title)</FormLabel>
                <FormControl>
                  <Input placeholder="Enter product name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="SgstPer"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">SGST%</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" placeholder="0.00" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="CGstPer"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">CGST%</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" placeholder="0.00" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="IgstPer"
            render={({ field }) => (
              <FormItem>
                <FormLabel>IGST%</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" placeholder="0.00" {...field} readOnly className="bg-gray-50" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="CessPer"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">Cess%</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" placeholder="0.00" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="MRP"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">SP Inc GST (MRP)</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01" 
                    placeholder="0" 
                    {...field}
                    onChange={(e) => {
                      lastChangedField.current = 'MRP';
                      field.onChange(e);
                    }}
                  />
                </FormControl>
                <p className="text-xs text-gray-500 mt-1">
                  Price including GST. Can be entered directly or calculated from SP Excl GST.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="SPExclGST"
            render={({ field }) => (
              <FormItem>
                <FormLabel>SP Excl GST</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01" 
                    placeholder="0" 
                    {...field}
                    onChange={(e) => {
                      lastChangedField.current = 'SPExclGST';
                      field.onChange(e);
                    }}
                  />
                </FormControl>
                <p className="text-xs text-gray-500 mt-1">
                  Price excluding GST. Can be entered directly or calculated from MRP.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="CostPrice"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Excl GST Cost Price</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" placeholder="0" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="NetQty"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Basic Price (NetQty)</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" placeholder="0" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="UQCName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">UQC</FormLabel>
                <FormControl>
                  <select className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" {...field}>
                    {UQC_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Category"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">Category (Name)</FormLabel>
                <FormControl>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                    value={field.value || ''}
                  >
                    <option value="">Select category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.Category}>{category.Category}</option>
                    ))}
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Barcode"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">Barcode</FormLabel>
                <FormControl>
                  <div className="flex gap-2">
                    <Input placeholder="Scan or enter barcode" {...field} />
                    <Button type="button" variant="outline" size="sm" onClick={generateBarcode}>Generate</Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="DiscountPer"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Discount%</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" placeholder="0" {...field} readOnly={Discount > 0} className={Discount > 0 ? 'bg-gray-50' : ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Discount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Discount (in rupees)</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" placeholder="0" {...field} readOnly={DiscountPer > 0} className={DiscountPer > 0 ? 'bg-gray-50' : ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="space-y-2">
            <Label>Offer Price</Label>
            <Input type="number" step="0.01" value={calculateOfferPrice().toFixed(2)} readOnly className="bg-gray-50" />
          </div>
          <FormField
            control={form.control}
            name="HSN"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">HSN/SAC</FormLabel>
                <FormControl>
                  <Input placeholder="1000" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="BrandName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Brand</FormLabel>
                <FormControl>
                  <Input placeholder="Enter brand name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Size"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Size</FormLabel>
                <FormControl>
                  <Input placeholder="Enter size" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Articlecode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Article Code</FormLabel>
                <FormControl>
                  <Input placeholder="Enter article code" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Color"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Color</FormLabel>
                <FormControl>
                  <Input placeholder="Enter color" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Style"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Shelf</FormLabel>
                <FormControl>
                  <Input placeholder="Enter shelf" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Stylecode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rack</FormLabel>
                <FormControl>
                  <Input placeholder="Enter rack" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {mode !== 'edit' && (
            <FormField
              control={form.control}
              name="Available"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-red-600">Available</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="0" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name="MinStQty"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-red-600">ROL</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="0" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Gender"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Gender</FormLabel>
                <FormControl>
                  <select
                    {...field}
                    value={typeof field.value === 'string' ? field.value : ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    className="w-full p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Option</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {mode !== 'edit' && (
            <FormField
              control={form.control}
              name="LocationID"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-red-600">Location</FormLabel>
                  <FormControl>
                    <select 
                      className="w-full p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={field.value === 0 ? '' : field.value || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          field.onChange(0);
                        } else {
                          const numericValue = Number(value);
                          field.onChange(numericValue);
                        }
                      }}
                    >
                      <option value="">Select location</option>
                      {locations.map(location => (
                        <option key={location.ID} value={location.ID}>{location.Name}</option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox id="isService" checked={form.watch('isService')} onCheckedChange={(checked) => form.setValue('isService', checked as boolean)} />
            <Label htmlFor="isService">Service</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="hasSerial" checked={form.watch('hasSerial')} onCheckedChange={(checked) => form.setValue('hasSerial', checked as boolean)} />
            <Label htmlFor="hasSerial">Has Serial Nos.</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="isTraceable" checked={form.watch('isTraceable')} onCheckedChange={(checked) => form.setValue('isTraceable', checked as boolean)} />
            <Label htmlFor="isTraceable">Trackable Inventory</Label>
          </div>
        </div>

        {showFooter && mode !== 'edit' && (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClear}>Clear</Button>
            <Button type="button" variant="outline" onClick={handleCancel}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : 'Add'}
            </Button>
          </DialogFooter>
        )}
      </form>
    </Form>
  );
});

export default SaleProductEntryForm;

export type SaleProductEntryFormHandle = {
  submit: () => void;
};